import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
  MenuItem,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, PersonAdd as PersonAddIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../providers/TenantProvider';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

type PipelineStage = 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';

interface Lead {
  id: string;
  tenantId: string;
  contact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string | null;
  };
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  pipelineStage: PipelineStage;
  source?: string | null;
  notes?: string | null;
  tags?: string[];
  nextActionAt?: { toDate?: () => Date } | string | null;
  nextActionDate?: string | null;
  estimatedValue?: number | null;
  createdAt: any;
  updatedAt: any;
}

interface Task {
  id: string;
  leadId: string;
  tenantId: string;
  title: string;
  completed: boolean;
  dueDate?: string;
  createdAt: any;
}

interface Note {
  id: string;
  leadId: string;
  tenantId: string;
  content: string;
  createdBy: string;
  createdAt: any;
}

const PIPELINE_STAGES: Array<{ id: PipelineStage; label: string; color: string }> = [
  { id: 'new', label: 'New', color: '#9e9e9e' },
  { id: 'contacted', label: 'Contacted', color: '#2196f3' },
  { id: 'qualified', label: 'Qualified', color: '#ff9800' },
  { id: 'proposal', label: 'Proposal', color: '#9c27b0' },
  { id: 'negotiation', label: 'Negotiation', color: '#f44336' },
  { id: 'won', label: 'Won', color: '#4caf50' },
  { id: 'lost', label: 'Lost', color: '#424242' },
];

function getLeadContact(lead: Lead) {
  return {
    firstName: lead.contact?.firstName || lead.firstName || '',
    lastName: lead.contact?.lastName || lead.lastName || '',
    email: lead.contact?.email || lead.email || '',
    phone: lead.contact?.phone || lead.phone || '',
  };
}

function getLeadNextActionDate(lead: Lead): Date | null {
  const value = lead.nextActionAt || lead.nextActionDate;
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateInputValue(date: Date | null): string {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
}

export default function LeadDetailPage() {
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();
  const { tenantId } = useTenant();
  const [lead, setLead] = useState<Lead | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: '',
    pipelineStage: 'new' as PipelineStage,
    estimatedValue: '',
    nextActionDate: '',
    notes: '',
  });
  const [taskInput, setTaskInput] = useState({ title: '', dueDate: '' });
  const [noteInput, setNoteInput] = useState('');

  useEffect(() => {
    if (!leadId || !tenantId) return;

    const loadLead = async () => {
      try {
        const leadDoc = await getDoc(doc(db, 'leads', leadId));
        if (!leadDoc.exists()) {
          alert('Lead not found');
          navigate('/leads');
          return;
        }

        const leadData = { id: leadDoc.id, ...leadDoc.data() } as Lead;
        if (leadData.tenantId !== tenantId) {
          alert('Access denied');
          navigate('/leads');
          return;
        }

        const contact = getLeadContact(leadData);
        setLead(leadData);
        setFormData({
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone || '',
          source: leadData.source || '',
          pipelineStage: leadData.pipelineStage,
          estimatedValue: leadData.estimatedValue?.toString() || '',
          nextActionDate: toDateInputValue(getLeadNextActionDate(leadData)),
          notes: leadData.notes || '',
        });
        setLoading(false);
      } catch (error) {
        console.error('Error loading lead:', error);
        alert('Failed to load lead');
        navigate('/leads');
      }
    };

    loadLead();
  }, [leadId, tenantId, navigate]);

  useEffect(() => {
    if (!leadId || !tenantId) return;

    const tasksQuery = query(
      collection(db, 'leadTasks'),
      where('leadId', '==', leadId),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const tasksData: Task[] = [];
      snapshot.forEach((doc) => {
        tasksData.push({ id: doc.id, ...doc.data() } as Task);
      });
      setTasks(tasksData);
    });

    return () => unsubscribe();
  }, [leadId, tenantId]);

  useEffect(() => {
    if (!leadId || !tenantId) return;

    const notesQuery = query(
      collection(db, 'leadNotes'),
      where('leadId', '==', leadId),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
      const notesData: Note[] = [];
      snapshot.forEach((doc) => {
        notesData.push({ id: doc.id, ...doc.data() } as Note);
      });
      setNotes(notesData);
    });

    return () => unsubscribe();
  }, [leadId, tenantId]);

  const handleUpdateLead = async () => {
    if (!leadId || !lead) return;

    try {
      await updateDoc(doc(db, 'leads', leadId), {
        contact: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
        },
        source: formData.source.trim() || null,
        pipelineStage: formData.pipelineStage,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
        nextActionAt: formData.nextActionDate ? Timestamp.fromDate(new Date(`${formData.nextActionDate}T12:00:00`)) : null,
        nextActionDate: formData.nextActionDate || null,
        notes: formData.notes.trim() || null,
        updatedAt: serverTimestamp(),
      });

      setEditDialogOpen(false);
      alert('Lead updated successfully');
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Failed to update lead');
    }
  };

  const handleAddTask = async () => {
    if (!leadId || !tenantId || !auth.currentUser || !taskInput.title.trim()) return;

    try {
      await addDoc(collection(db, 'leadTasks'), {
        leadId,
        tenantId,
        title: taskInput.title.trim(),
        completed: false,
        dueDate: taskInput.dueDate || null,
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
      });

      setTaskDialogOpen(false);
      setTaskInput({ title: '', dueDate: '' });
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Failed to add task');
    }
  };

  const handleToggleTask = async (taskId: string, completed: boolean) => {
    try {
      await updateDoc(doc(db, 'leadTasks', taskId), {
        completed: !completed,
      });
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteDoc(doc(db, 'leadTasks', taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleAddNote = async () => {
    if (!leadId || !tenantId || !auth.currentUser || !noteInput.trim()) return;

    try {
      await addDoc(collection(db, 'leadNotes'), {
        leadId,
        tenantId,
        content: noteInput.trim(),
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
      });

      setNoteDialogOpen(false);
      setNoteInput('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  const handleConvertToMember = async () => {
    if (!leadId) return;

    try {
      const convertLead = httpsCallable(functions, 'convertLeadToMember');
      const result = await convertLead({ leadId });
      
      const data = result.data as { success: boolean; memberId: string; stripeCustomerId?: string };
      
      if (data.success) {
        alert(`Successfully converted to member! ${data.stripeCustomerId ? 'Stripe customer created.' : ''}`);
        setConvertDialogOpen(false);
        navigate(`/members/${data.memberId}`);
      } else {
        alert('Conversion failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Error converting lead:', error);
      alert(`Failed to convert lead: ${error.message || 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading lead...</Typography>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Lead not found</Typography>
      </Container>
    );
  }

  const stageInfo = PIPELINE_STAGES.find((s) => s.id === lead.pipelineStage);
  const leadContact = getLeadContact(lead);
  const leadNextActionDate = getLeadNextActionDate(lead);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => navigate('/leads')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">
              {[leadContact.firstName, leadContact.lastName].filter(Boolean).join(' ') || 'Unnamed Lead'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lead Details
            </Typography>
          </Box>
          <Chip
            label={stageInfo?.label}
            sx={{ bgcolor: stageInfo?.color, color: 'white' }}
          />
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setConvertDialogOpen(true)}
            disabled={lead.pipelineStage === 'won' || lead.pipelineStage === 'lost'}
          >
            Convert to Member
          </Button>
          <IconButton onClick={() => setEditDialogOpen(true)}>
            <EditIcon />
          </IconButton>
        </Stack>

        {/* Contact Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography>{leadContact.email || 'N/A'}</Typography>
              </Box>
              {leadContact.phone && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography>{leadContact.phone}</Typography>
                </Box>
              )}
              {lead.source && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Source
                  </Typography>
                  <Typography>{lead.source}</Typography>
                </Box>
              )}
              {lead.estimatedValue && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Monthly Value
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ${lead.estimatedValue.toLocaleString()}
                  </Typography>
                </Box>
              )}
              {leadNextActionDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Next Action Date
                  </Typography>
                  <Typography>
                    {leadNextActionDate.toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Tasks</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setTaskDialogOpen(true)}
              >
                Add Task
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {tasks.length === 0 ? (
              <Typography color="text.secondary">No tasks yet</Typography>
            ) : (
              <List>
                {tasks.map((task) => (
                  <ListItem
                    key={task.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleDeleteTask(task.id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={task.completed}
                        onChange={() => handleToggleTask(task.id, task.completed)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={task.title}
                      secondary={task.dueDate ? `Due: ${new Date(task.dueDate).toLocaleDateString()}` : null}
                      sx={{
                        textDecoration: task.completed ? 'line-through' : 'none',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Notes</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNoteDialogOpen(true)}
              >
                Add Note
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {notes.length === 0 ? (
              <Typography color="text.secondary">No notes yet</Typography>
            ) : (
              <Stack spacing={2}>
                {notes.map((note) => (
                  <Paper key={note.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{note.content}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {note.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Lead</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Source"
              value={formData.source}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, source: e.target.value })}
              fullWidth
            />
            <TextField
              label="Pipeline Stage"
              select
              value={formData.pipelineStage}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, pipelineStage: e.target.value as PipelineStage })}
              fullWidth
            >
              {PIPELINE_STAGES.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Estimated Value"
              type="number"
              value={formData.estimatedValue}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, estimatedValue: e.target.value })}
              InputProps={{ startAdornment: '$' }}
              fullWidth
            />
            <TextField
              label="Next Action Date"
              type="date"
              value={formData.nextActionDate}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, nextActionDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateLead}
            variant="contained"
            disabled={!formData.firstName || !formData.lastName || !formData.email}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Task Title"
              value={taskInput.title}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTaskInput({ ...taskInput, title: e.target.value })}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Due Date"
              type="date"
              value={taskInput.dueDate}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTaskInput({ ...taskInput, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddTask}
            variant="contained"
            disabled={!taskInput.title.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            label="Note"
            value={noteInput}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNoteInput(e.target.value)}
            multiline
            rows={4}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddNote}
            variant="contained"
            disabled={!noteInput.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Member Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert Lead to Member</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a new member from this lead and optionally create a Stripe customer.
            The lead will be marked as "Won".
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Member Name: {[leadContact.firstName, leadContact.lastName].filter(Boolean).join(' ') || 'Unnamed Lead'}
          </Typography>
          <Typography color="text.secondary">
            Email: {leadContact.email || 'N/A'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConvertToMember}
            variant="contained"
            color="primary"
          >
            Convert to Member
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
